enum PermissionState {
    Granted, Denied, UnknownDueToOldBrowser
}

export const RecordingUnavailableNoCapabilitiesError = new Error("RecordingUnavailableNoCapabilitiesError");
export const RecordingUnavailableBrowserNoMicrophoneError = new Error("RecordingUnavailableBrowserNoMicrophoneError");
export const RecordingUnavailableDenied = new Error("RecordingUnavailableDenied");
export const RecordingUnavailableAnotherAppRecording = new Error("RecordingUnavailableAnotherAppRecording");
export const RecordingUnavailableMicrophoneIsMuted = new Error("RecordingUnavailableMicrophoneIsMuted");

export interface Configuration {
    onRecordingTerminallyFailed: (e: Error) => void
    onRecordingTerminated: (blob: Blob) => void
    onChunkLengthUpdate: (length: number) => void
}

export class RecordingManager {
    private mediaRecorder?: MediaRecorder
    private terminalPermissionState?: PermissionState;
    private readonly configuration: Configuration;
    private recordedChunks: Blob[] = [];
    private chunkRecordingStartDate?: Date
    private allChunksLength = 0;
    private chunkLengthUpdateTimer?: number;
    private isResetting = false;

    constructor(config: Configuration) {
        this.configuration = config;
        this.poolUntilMicrophonePermissionStateIsTerminal().then((permissionState) => {
            this.terminalPermissionState = permissionState;
        });
    }

    async record(): Promise<MediaStream> {
        try {
            if (this.mediaRecorder) {
                this.mediaRecorder.resume();
            } else {
                await this.createMediaRecorderIfNeeded();
                (this.mediaRecorder as unknown as MediaRecorder).start();
            }
            this.chunkRecordingStartDate = new Date();

            this.chunkLengthUpdateTimer = setInterval(() => {
                if (!this.chunkRecordingStartDate) {
                    return;
                }

                const chunkLength = new Date().getTime() - this.chunkRecordingStartDate.getTime();
                this.configuration.onChunkLengthUpdate(chunkLength)
            }, 100)

            return (this.mediaRecorder as unknown as MediaRecorder).stream;
        } catch (e: unknown) {
            this.stopStreamAndRemoveListeners();
            this.configuration.onRecordingTerminallyFailed(e as Error);
            throw e;
        }
    }

    pauseRecording(): number {
        this.mediaRecorder?.pause();

        if (this.chunkLengthUpdateTimer) {
            clearInterval(this.chunkLengthUpdateTimer);
            this.chunkLengthUpdateTimer = undefined;
        }

        if (!this.chunkRecordingStartDate) {
            throw new Error("Unexpectedly chunkRecordingStartDate is undefined")
        }

        const chunkLength = new Date().getTime() - this.chunkRecordingStartDate.getTime();
        this.allChunksLength += chunkLength;
        return this.allChunksLength / 1000.0;
    }

    terminate() {
        this.isResetting = false;
        this.mediaRecorder?.stop();
    }

    reset(): void {
        this.isResetting = true;
        this.mediaRecorder?.stop();
    }

    // Private -

    private async createMediaRecorderIfNeeded() {
        if (this.mediaRecorder !== undefined) {
            return false;
        }

        this.mediaRecorder = await this.makeMediaRecorder()
        while (this.terminalPermissionState === undefined) {
            await new Promise(r => setTimeout(r, 100));
        }

        if (this.terminalPermissionState == PermissionState.Denied) {
            throw RecordingUnavailableDenied;
        }

        if (this.mediaRecorder.stream.getAudioTracks().length < 1) {
            throw RecordingUnavailableAnotherAppRecording;
        }

        if (this.mediaRecorder.stream.getAudioTracks().length > 0 && this.mediaRecorder.stream.getAudioTracks()[0].muted) {
            throw RecordingUnavailableMicrophoneIsMuted;
        }
    }

    private async makeMediaRecorder(): Promise<MediaRecorder> {
        if (this.firstSupportedMediaType === undefined) {
            throw RecordingUnavailableNoCapabilitiesError;
        }

        let stream: MediaStream
        try {
            stream = await window.navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        } catch (e) {
            console.error("MediaStream can't be created");
            console.error(e);
            // An error at this point can mean:
            // a) browser itself, or the app hosting the in-app browser (such as LinkedIn), doesn't
            //    even have permissions to prompt the user. In such case the permissions state will stay
            //    'prompt' forever, but the stream will fail to be created. User never sees  the prompt.
            // b) older browsers, such as Safari iOS 15, don't have the permissions API, so the only way to
            //    learn about permission rejected is by an error throw in stream creation here
            throw RecordingUnavailableBrowserNoMicrophoneError;
        }

        const mediaRecorder: MediaRecorder = new MediaRecorder(stream, { mimeType: this.firstSupportedMediaType });
        mediaRecorder.addEventListener('dataavailable', this.mediaRecorderDataAvailableEventHandler.bind(this));
        mediaRecorder.addEventListener('stop', this.mediaRecorderStopEventHandler.bind(this));

        mediaRecorder.onerror = (error) => {
            console.error('Error reported by the MediaRecorder');
            console.error(error);
        };

        return mediaRecorder;
    }

    // Only Chrome browser currently allows us to subscribe to changes, so we need to pool
    // New API to listen to permission changes (onchange) is not working in Safari, so the
    // microphone state is never updated in the case of using it.
    // The solution now is to ask for the status until we get the answer (granted, denied)
    // and then act accordingly
    private poolUntilMicrophonePermissionStateIsTerminal(): Promise<PermissionState> {
        return new Promise((resolve) => {
            const interval = setInterval(async () => {
                if (window.navigator.permissions && window.navigator.permissions.query) {
                    try {
                        const permissionName = "microphone" as PermissionName;
                        const result = await window.navigator.permissions.query({ name: permissionName });
                        switch (result.state) {
                            case 'denied':
                                clearInterval(interval);
                                resolve(PermissionState.Denied)
                                break;
                            case 'granted':
                                clearInterval(interval);
                                resolve(PermissionState.Granted)
                                break;
                            case 'prompt':
                            default:
                                // do nothing, keep pooling
                        }
                    } catch (e) {
                        console.error('Unable to inspect navigator permissions');
                        console.error(e);

                        // Firefox supports window.navigator.permissions, but not for microphone. So, querying will throw
                        // an error, and we can safely assume we'll never know at this point
                        clearInterval(interval);
                        resolve(PermissionState.UnknownDueToOldBrowser)
                    }
                } else {
                    // older browser such as Safari iOS 15 don't support navigator permissions API. The only
                    // possible way to detect permission rejection is to try to create MediaStream and catch it fail.
                    clearInterval(interval);
                    resolve(PermissionState.UnknownDueToOldBrowser)
                }
            }, 100);
        });
    }


    private get firstSupportedMediaType(): string | undefined {
        return ["audio/mp4", "audio/webm"].find((contentType) => MediaRecorder?.isTypeSupported(contentType));
    }

    private mediaRecorderDataAvailableEventHandler(event: BlobEvent): void {
        if (event.data.size > 0) {
            this.recordedChunks?.push(event.data);
        }
    }

    private mediaRecorderStopEventHandler() {
        if (this.recordedChunks && this.recordedChunks.length > 0 && !this.isResetting) {
            const recording = new Blob(this.recordedChunks, { type: this.firstSupportedMediaType });
            this.configuration.onRecordingTerminated(recording);
        }
        this.stopStreamAndRemoveListeners();
    }

    private stopStreamAndRemoveListeners(): void {
        this.mediaRecorder?.removeEventListener('dataavailable', this.mediaRecorderDataAvailableEventHandler);
        this.mediaRecorder?.removeEventListener('stop', this.mediaRecorderStopEventHandler);
        this.mediaRecorder?.stream.getTracks().forEach((track) => track.stop());
        this.mediaRecorder = undefined;

        if (this.isResetting) {
            this.recordedChunks = [];
            this.chunkRecordingStartDate = undefined;
            this.allChunksLength = 0;
            this.chunkLengthUpdateTimer = undefined;
            this.isResetting = false;
        }
    }
}
